import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getEmailTemplateById, updateEmailTemplate } from "../../Services/EmailTemplate";
import { getBeefreeToken } from "../../Services/Beefree";
import { IEmailTemplate } from "../../interfaces/models/IEmailTemplate";
import { ensureAbsoluteImageUrls } from "../../utils/emailTemplateHtml";
import { toastSuccess, toastError } from "../../utils/toast";

const BEEFREE_CONTAINER_ID = "beefree-editor-container";

const BEEFREE_MERGE_TAGS = [
  { name: "First Name", value: "{{FirstName}}" },
  { name: "Last Name", value: "{{LastName}}" },
  { name: "Firm", value: "{{FirmName}}" },
  { name: "Member Name (full)", value: "{{MemberName}}" },
  { name: "Email", value: "{{Email}}" },
  { name: "Unsubscribe URL (this email type)", value: "{{UnsubscribeUrl}}" },
  { name: "Unsubscribe URL (all / GDPR)", value: "{{UnsubscribeAllUrl}}" },
];


export default function BeefreeEditorPage(): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [template, setTemplate] = useState<IEmailTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const beeInstanceRef = useRef<{ save: () => void } | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const shouldRedirectAfterSaveRef = useRef(false);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      setError("Missing template id");
      return;
    }

    let cancelled = false;

    const init = async () => {
      try {
        const [token, data] = await Promise.all([
          getBeefreeToken(),
          getEmailTemplateById(id),
        ]);
        if (cancelled) {
          return;
        }
        if (!data) {
          setError("Template not found");
          setLoading(false);
          return;
        }
        setTemplate(data);

        const el = document.getElementById(BEEFREE_CONTAINER_ID);
        if (!el) {
          setError("Editor container not found");
          setLoading(false);
          return;
        }
        await new Promise((r) => requestAnimationFrame(r));
        if (cancelled) {
          return;
        }

        const BeefreeSDK = (await import("@beefree.io/sdk")).default;
        const sdkToken = { ...token, v2: token.v2 ?? true };
        const bee = new BeefreeSDK(sdkToken);

        let initialDesign: object = {};
        if (data.beefreeJson?.trim()) {
          try {
            initialDesign = JSON.parse(data.beefreeJson) as object;
          } catch {
            initialDesign = {};
          }
        }

        const beeConfig = {
          container: BEEFREE_CONTAINER_ID,
          autosave: false as const,
          mergeTags: BEEFREE_MERGE_TAGS,
          onSave: (jsonFile: string, htmlFile: string) => {
            if (!data.id) {
              return;
            }
            setSaving(true);
            const imageBaseUrl =
              process.env.REACT_APP_EMAIL_IMAGE_BASE_URL?.trim() ||
              (typeof window !== "undefined" ? window.location.origin : "");
            const htmlWithAbsoluteImages = ensureAbsoluteImageUrls(htmlFile, imageBaseUrl);
            updateEmailTemplate({
              ...data,
              template: htmlWithAbsoluteImages,
              beefreeJson: jsonFile,
            })
              .then(() => {
                setSaving(false);
                toastSuccess("Design saved successfully.");
                if (shouldRedirectAfterSaveRef.current) {
                  shouldRedirectAfterSaveRef.current = false;
                  navigate(`/email-design/${data.id}`);
                }
              })
              .catch((err: unknown) => {
                setSaving(false);
                toastError(err instanceof Error ? err.message : "Failed to save design.");
              });
          },
          onError: (err: unknown) => {
            setError(err instanceof Error ? err.message : String(err));
          },
        };

        const instance = await bee.start(beeConfig, initialDesign);
        if (!cancelled && instance) {
          beeInstanceRef.current = instance as { save: () => void };
        }
      } catch (e) {
        if (!cancelled) {
          const msg = e instanceof Error ? e.message : String(e);
          setError(msg || "Failed to load Beefree editor");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    init();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleSave = () => {
    shouldRedirectAfterSaveRef.current = true;
    beeInstanceRef.current?.save();
  };

  const handleBack = () => {
    setShowLeaveModal(true);
  };

  const handleLeaveConfirm = () => {
    setShowLeaveModal(false);
    navigate(id ? `/email-design/${id}` : "/email-design");
  };

  if (error) {
    return (
      <main className="p-6 max-w-xl">
        <p className="text-red-600 font-medium">Beefree editor could not load</p>
        <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">{error}</p>
        <button type="button" onClick={handleBack} className="mt-4 text-Default underline hover:no-underline">
          ← Back
        </button>
      </main>
    );
  }

  return (
    <main className="p-6 bg-white">
      {showLeaveModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="leave-modal-title"
          onClick={() => setShowLeaveModal(false)}
        >
          <div
            className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="leave-modal-title" className="text-lg font-semibold text-gray-900">
            Are you sure you want to leave the editor? Unsaved changes can be lost.
            </h2>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowLeaveModal(false)}
                className="px-4 py-2 rounded border border-gray-300 bg-white text-gray-700 font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleLeaveConfirm}
                className="px-4 py-2 rounded bg-Default text-white font-medium hover:opacity-90"
              >
                Leave
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between gap-4 mb-4">
        <button type="button" onClick={handleBack} className="text-Default hover:underline">
          ← Back to template
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 bg-Default text-white rounded font-medium hover:opacity-90 disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save design"}
        </button>
      </div>
      <div className="relative w-full rounded border border-gray-200">
        <div
          ref={containerRef}
          id={BEEFREE_CONTAINER_ID}
          className="w-full border-0 rounded"
          style={{ height: 600 }}
        />
        {loading && (
          <div
            className="absolute inset-0 flex items-center justify-center rounded bg-gray-50"
            style={{ top: 0, left: 0, right: 0, bottom: 0 }}
            aria-hidden
          >
            <p className="text-gray-600">Loading Beefree editor…</p>
          </div>
        )}
      </div>
    </main>
  );
}
